import React, { PureComponent } from 'react';
import memoizeOne from 'memoize-one';
import { MapContextMenu, MapContextMenuProps } from 'app/components/dashboard/network/flows/menu/MapContextMenu';
import {
    MapLayerUtils,
    MapObjectType,
    MeasureImpactLayerUtils,
    SolutionTvImpact,
    TvImpactLayerUtils,
    WeatherLayerUtils,
} from 'app/components/dashboard/network/map/Map';
import { VALIDITY_PERIOD_PROPERTY_NAME } from 'app/components/map/ol/plugin/TvConvectionMapDisplayPlugin';
import { MapPendingEventsComponent } from 'app/components/dashboard/network/map/MapPendingEventsComponent';
import {
    DEFAULT_MEASURES_SETTINGS,
    MeasuresSettings,
} from 'app/components/dashboard/detailed/measures/MeasuresSettings';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import { TV_PROPERTY_NAME } from 'app/components/map/ol/plugin/PolygonConvectionMapDisplayPlugin';
import MapUtils from 'app/components/map/util/MapUtils';
import { TabManager } from 'app/components/common/TabManager';
import { Drawer, notification, Spin } from 'antd';
import {
    DEFAULT_EVENTS_TV_SETTINGS,
    EventsTvFilterSettings,
    EventsTvFilterSettingsButton,
} from 'app/components/dashboard/filtering/settings/EventsTvFilterSettings';
import {
    DEFAULT_TV_EVENTS_SETTINGS,
    TvEventsFilterSettings,
    TvEventsFilterSettingsButton,
} from 'app/components/dashboard/filtering/settings/TvEventsFilterSettings';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import { RootState, store } from 'app/redux/store';
import { connect, ConnectedProps } from 'react-redux';
import {
    getConfigData,
    getDashboardDataForCurrentVersion,
    getDashboardFilteredData,
    getDataVersion,
    selectConvectionData,
    selectDashboardData,
    setDashboardData,
    setDataVersion,
    setDrawerOpen,
    setPartialDashboardData,
    setTextFilter,
} from 'app/redux/features/dashboard/dashboard-slice';
import MapTrafficVolumeCacheComponent from 'app/components/dashboard/network/map/MapTrafficVolumeCache';
import { DEFAULT_HOTSPOT_SETTINGS, HotspotSettings } from 'app/components/dashboard/detailed/hotspots/HotspotSettings';
import ChatPanel from 'app/components/dashboard/chat/ChatPanel';
import TimeUtils from 'app/common/util/TimeUtils';
import { PushpinFilled, PushpinOutlined } from '@ant-design/icons';
import { PayloadAction } from '@reduxjs/toolkit';
import { setMapSettings } from 'app/redux/features/map/settings-slice';
import { setCurrentTime, setMonitoredPeriod } from 'app/redux/features/dashboard/time-slice';
import { MonitoredEventPanelWrapper } from 'app/components/panels/predict/MonitoredEventPanelWrapper';
import { DEFAULT_MONITORED_EVENTS_SETTINGS } from 'app/components/dashboard/global/events/MonitoredEventsSettings';
import { DEFAULT_FL_FILTER_RANGE } from 'app/components/dashboard/network/AbstractMapView';
import { ClearButton } from 'app/components/dashboard/filtering/ClearButton';
import { GlobalMonitoringV2 } from 'app/components/dashboard/global/GlobalMonitoringV2';
import { NMUI4DContext, NMUI4DContextProps } from 'app/common/context/NMUI4DContext';
import { ProfileSegmentHighlight } from 'app/components/map/model/MapModel';

import {
    DashboardDataReply,
    DashboardDataRequest,
    DashboardFilteredData,
    Entity,
    ExtendedMonitoredTv,
    FilteredFlightExtraData,
    MilitaryZoneDto,
    MonitoredEvent,
    MonitoredMcdmMeasure,
    MonitoredMeasure,
    MonitoredRegulationOutlook,
    NetworkDashboardData,
    NiaMeasureImpact,
    OverlappingFilter,
    PolygonMeteoForecast,
    SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE,
    TrafficVolumeMeteoForecast,
} from '../model/SOL49';
import {
    ConfigData,
    CountType,
    DatasetType,
    FlightDto,
    FlightId,
    FlightLevelBand,
    HotspotDto,
    MonitoredLocationType,
    Reply,
    TimePeriod,
    TrafficType,
} from '../model/CORE';
import { FilterComponent } from '../components/dashboard/filtering/FilterComponent';
import {
    DEFAULT_TV_FLIGHTS_SETTINGS,
    TvFlightsFilterSettings,
    TvFlightsFilterSettingsButton,
} from '../components/dashboard/filtering/settings/TvFlightsFilterSettings';
import {
    DEFAULT_FLIGHTS_TV_SETTINGS,
    FlightsTvFilterSettings,
    FlightsTvFilterSettingsButton,
} from '../components/dashboard/filtering/settings/FlightsTvFilterSettings';
import {
    DEFAULT_MEASURE_FLIGHTS_SETTINGS,
    MeasureFlightsFilterSettings,
    MeasureFlightsFilterSettingsButton,
} from '../components/dashboard/filtering/settings/MeasureFlightsFilterSettings';
import {
    DEFAULT_MEASURE_TV_SETTINGS,
    MeasureTvFilterSettings,
    MeasureTvFilterSettingsButton,
} from '../components/dashboard/filtering/settings/MeasureTvFilterSettings';
import {
    DEFAULT_TV_MEASURE_SETTINGS,
    TvMeasureFilterSettings,
    TvMeasureFilterSettingsButton,
} from '../components/dashboard/filtering/settings/TvMeasureFilterSettings';
import { DEFAULT_MAP_SETTINGS, MapSettings } from '../components/dashboard/network/map/MapSettings';
import {
    DEFAULT_FLIGHT_LIST_FILTER_SETTINGS,
    FlightListFilterSettings,
} from '../components/dashboard/detailed/flights/FlightsSettings';
import AppContext from '../common/context/AppContext';
import {
    DEFAULT_FLIGHTS_MEASURE_SETTINGS,
    FlightsMeasureFilterSettings,
    FlightsMeasureFilterSettingsButton,
} from '../components/dashboard/filtering/settings/FlightsMeasureFilterSettings';
import { ServiceFactory } from '../service/ServiceFactory';
import {
    CoordinationSummaryUpdateEvent,
    DashboardSettingsUpdateEvent,
    MapElementClickEvent,
    MonitoringPeriodSettingsChange,
    OpenTvMonitoredEvents,
    PartialDashboardDataUpdateEvent,
    ShowModalEvent,
    ShowTvDetailEvent,
    UpdateFlightSelection,
    UpdateHotspotSelection,
    UpdateMeasureSelection,
    UpdateRegulationOutlookSelection,
    UpdateTvSelection,
} from '../common/eventbus/Events';
import Utils from '../common/util/Utils';
import { TableUtils } from '../common/util/TableUtils';
import KeyUtils from '../common/util/KeyUtils';
import FlightUtils from '../common/util/FlightUtils';
import MeasureUtils from '../common/util/MeasureUtils';
import { Flex } from '../components/common/Flex';
import { SvgCommonDefs } from '../components/dashboard/network/flows/svg/SvgCommonDefs';
import { Flights } from '../components/dashboard/detailed/flights/Flights';
import { DashboardModal } from '../components/panels/DashboardModal';
import {
    FilterButtonDirection,
    OverlappingFilterButtonDetail,
    OverlappingFilterUtils,
} from '../common/util/OverlappingFilterUtils';
import './Dashboard.less';
import { NmocNetworkMonitoring } from '../components/dashboard/network/NmocNetworkMonitoring';
import { DashboardUtils } from './DashboardUtils';
import { EntityType } from '../model/client/SOL49';
import ConditionalRender from '../common/components/ConditionalRender';
import AppConstants from '../common/context/AppConstants';
import { DashboardFlightUtils } from './DashboardFlightUtils';

const POLLING_REFRESH_INTERVAL_IN_MS = 5 * TimeUtils.SEC_TO_MS;
const MAX_ALLOWED_POLLING_COMMUNICATION_FAILURES = 20;

const mapStateToProps = (state: RootState) => {
    return {
        dataVersion: state.dashboardData.currentDataVersion,
        mapSettings: state.mapSettingsData.mapSettings,
        loadConvection: state.dashboardData.loadConvection,
        monitoredPeriod: state.timeData.monitoredPeriod,
        detailedSolutions: state.solutions.detailedSolutions,
        drawerOpen: state.dashboardData.drawerOpen,
        globalTextFilter: state.dashboardData.globalTextFilter,
    };
};

const mapDispatchToProps = {
    getConfigData,
    getDashboardDataForCurrentVersion,
    getDataVersion,
    setDataVersion,
    getDashboardFilteredData,
    setPartialDashboardData,
    setDashboardData,
    setMapSettings,
    setCurrentTime,
    setMonitoredPeriod,
    setDrawerOpen,
    setTextFilter,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type NmocDashboardProps = PropsFromRedux & {
    configData: ConfigData;
};

interface NmocDashboardState {
    loading: boolean;
    systemInfo?: string;
    logoutUrl?: string;
    displayedFlights: FlightDto[];
    filteredData?: DashboardFilteredData;
    currentTime: Date;
    showMap: boolean;
    fullscreenMap: boolean;
    selectedTvs: Array<ExtendedMonitoredTv>;
    selectedMeasures: Array<MonitoredMeasure>;
    selectedFlights: Array<FlightDto>;
    selectedFlightsOccupancyPeriods: { [key: string]: Map<FlightId, TimePeriod> };
    selectedHotspotTvs: Array<ExtendedMonitoredTv>;
    selectedRegOutlooks: Array<MonitoredRegulationOutlook>;
    selectedEvents: Array<MonitoredEvent>;
    tvMeasureFilterButtonDetails: OverlappingFilterButtonDetail<TvMeasureFilterSettings, MeasureTvFilterSettings>;
    tvFlightsFilterButtonDetails: OverlappingFilterButtonDetail<TvFlightsFilterSettings, FlightsTvFilterSettings>;
    eventTvFilterButtonDetails: OverlappingFilterButtonDetail<EventsTvFilterSettings, TvEventsFilterSettings>;
    measureFlightsFilterButtonDetails: OverlappingFilterButtonDetail<
        MeasureFlightsFilterSettings,
        FlightsMeasureFilterSettings
    >;
    flightListFilterSettings: FlightListFilterSettings;
    globalFilterEnabled: boolean;
    measureSettings: MeasuresSettings;
    hotspotSettings: HotspotSettings;
    chatPin: boolean;
    monitoredEvents: MonitoredEvent[];
}

export class NmocDashboard extends PureComponent<NmocDashboardProps, NmocDashboardState> {
    manualUpdate: boolean = false;
    monitoredZones: string[] = AppContext.getZones()?.flat(2);
    state = {
        loading: false,
        showMap: false,
        fullscreenMap: false,
        displayedFlights: [],
        selectedTvs: [],
        selectedMeasures: [],
        selectedFlights: [],
        selectedHotspotTvs: [],
        selectedRegOutlooks: [],
        selectedEvents: [],
        monitoredEvents: [],
        tvMeasureFilterButtonDetails: new OverlappingFilterButtonDetail(
            Entity.TV,
            Entity.MEASURE,
            DEFAULT_TV_MEASURE_SETTINGS,
            DEFAULT_MEASURE_TV_SETTINGS,
        ),
        tvFlightsFilterButtonDetails: new OverlappingFilterButtonDetail(
            Entity.TV,
            Entity.FLIGHT,
            DEFAULT_TV_FLIGHTS_SETTINGS,
            DEFAULT_FLIGHTS_TV_SETTINGS,
        ),
        measureFlightsFilterButtonDetails: new OverlappingFilterButtonDetail(
            Entity.MEASURE,
            Entity.FLIGHT,
            DEFAULT_MEASURE_FLIGHTS_SETTINGS,
            DEFAULT_FLIGHTS_MEASURE_SETTINGS,
        ),
        eventTvFilterButtonDetails: new OverlappingFilterButtonDetail(
            Entity.EVENT,
            Entity.TV,
            DEFAULT_EVENTS_TV_SETTINGS,
            DEFAULT_TV_EVENTS_SETTINGS,
        ),
        flightListFilterSettings: DEFAULT_FLIGHT_LIST_FILTER_SETTINGS,
        globalFilterEnabled: true,
        measureSettings: DEFAULT_MEASURES_SETTINGS,
        hotspotSettings: DEFAULT_HOTSPOT_SETTINGS,
        chatPin: false,
    } as NmocDashboardState;

    public static contextType = NMUI4DContext;
    private nmui4dContext?: NMUI4DContextProps;

    private flightUtils = new DashboardFlightUtils();
    private display = CountType.ENTRY;
    private highlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
    private mapFlightDataWithTvLoads = new Map<FlightId, ProfileSegmentHighlight[]>();
    private enrichedFlights: FlightDto[] = [];

    private flightLevelFilter: FlightLevelBand = DEFAULT_FL_FILTER_RANGE;
    private mapRefTimePeriod: TimePeriod;
    private convectionCacheKey?: {
        networkForecastLastUpdate: Date;
        flightLevelFilter: FlightLevelBand;
        mapRefTimePeriod: TimePeriod;
        mapSettings: MapSettings;
    };

    public constructor(props) {
        super(props);
        this.props.setMapSettings(DEFAULT_MAP_SETTINGS);
        this.initEventHandlers();

        this.mapRefTimePeriod = { from: AppContext.getCurrentTime(), to: AppContext.getMonitoringPeriod().to };
    }

    componentDidMount(): void {
        this.nmui4dContext = this.context as NMUI4DContextProps;

        this.initialise();
    }

    componentDidUpdate(prevProps: Readonly<NmocDashboardProps>) {
        if (prevProps.dataVersion !== this.props.dataVersion && !this.manualUpdate) {
            this.refreshDashboardData();
        }
        //if the redux state load convection was updated by any component, refresh the dashboard data so that component gets it updated
        else if (prevProps.loadConvection !== this.props.loadConvection) {
            //if the previous value was already positive then no need to refresh
            //ignore if not needed anymore (new value = 0), it will be clean in the cyclic update anyhow and so a fetch to the server is avoided
            if (prevProps.loadConvection === 0 && this.props.loadConvection > 0) {
                this.refreshDashboardData();
            }
        }
    }

    initEventHandlers = () => {
        AppContext.eventBus.register(MonitoringPeriodSettingsChange.NAME, (event: MonitoringPeriodSettingsChange) => {
            this.manualUpdate = true;
            const currentDataset = AppContext.getDataset();

            const deltaDay = event.monitoringPeriodSettings.deltaDay;
            if (deltaDay > 0 && AppContext.getDataset().type === DatasetType.OPERATIONAL) {
                AppContext.setDataset({ type: DatasetType.FORECAST });
            } else if (deltaDay < 1 && AppContext.getDataset().type === DatasetType.FORECAST) {
                AppContext.setDataset({ type: DatasetType.OPERATIONAL });
            }

            this.setState({ loading: true }, () => {
                if (currentDataset.type !== AppContext.getDataset().type) {
                    // Update config data, since we're targeting a different server, with potentially a different config
                    this.props
                        .getConfigData()
                        .then(async reply => {
                            const error = reply['error'];
                            const payload: Reply<ConfigData> = reply['payload'];
                            if (error) {
                                notification.error({
                                    message: `${AppContext.getDataset().type} data is not available`,
                                    description: error.message,
                                    duration: 0,
                                });
                                // Revert to old dataset ! (corresponding server not available)
                                AppContext.setDataset(currentDataset);
                                console.error('getConfigData failed:', error);
                                this.setState({ loading: false });
                                this.manualUpdate = false;
                            } else {
                                this.setState({ loading: true });
                                AppContext.configData = payload.data;
                                AppContext.setMonitoringPeriodSettings(event.monitoringPeriodSettings);
                                // Update data version, will trigger dashboard data update afterwards
                                await this.props.getDataVersion();
                                this.refreshDashboardData(() => {
                                    this.setState({ loading: false });
                                    this.manualUpdate = false;
                                });
                            }
                        })
                        .catch(reason => {
                            notification.error({ message: 'Error', description: reason?.message, duration: 0 });
                            // Revert to old dataset ! (corresponding server not available)
                            AppContext.setDataset(currentDataset);
                            console.error('getConfigData failed:', reason);
                            this.setState({ loading: false });
                            this.manualUpdate = false;
                        });
                } else {
                    // Same dataset, we just update monitored period and update dashboard data
                    AppContext.setMonitoringPeriodSettings(event.monitoringPeriodSettings);
                    this.refreshDashboardData(() => {
                        this.setState({ loading: false });
                        this.manualUpdate = false;
                    });
                }
            });
        });
        AppContext.eventBus.register(DashboardSettingsUpdateEvent.NAME, (event: DashboardSettingsUpdateEvent) => {
            const wcmChanged = AppContext.dashboardSettings.capacityModel !== event.dashboardSettings.capacityModel;
            const convectionSourceChanged =
                AppContext.dashboardSettings.convectionSource !== event.dashboardSettings.convectionSource;
            AppContext.setDashboardSettings(event.dashboardSettings);
            if (wcmChanged || convectionSourceChanged) {
                this.manualUpdate = true;
                this.setState({ loading: true }, () =>
                    this.refreshDashboardData(() => {
                        this.setState({ loading: false });
                        this.manualUpdate = false;
                    }),
                );
            }
        });
        AppContext.eventBus.register(UpdateFlightSelection.NAME, (event: UpdateFlightSelection) => {
            const selectedFlights =
                event.action === 'replace'
                    ? event.selection
                    : Utils.copyAndAddAllIfNeeded(this.state.selectedFlights, event.selection);
            this.handleFlightSelectionChange(selectedFlights);
        });
        AppContext.eventBus.register(UpdateMeasureSelection.NAME, (event: UpdateMeasureSelection) => {
            const selectedMeasures =
                event.action === 'replace'
                    ? event.selection
                    : Utils.copyAndAddAllIfNeeded(this.state.selectedMeasures, event.selection);
            this.handleMeasureSelectionChange(selectedMeasures);
        });
        AppContext.eventBus.register(UpdateHotspotSelection.NAME, (event: UpdateHotspotSelection) => {
            const selectedHotspotsTvs: ExtendedMonitoredTv[] = event.action.replace
                ? event.selection
                : Utils.copyAndAddAllIfNeeded(this.state.selectedHotspotTvs, event.selection);
            this.handleHotspotSelectionChange(selectedHotspotsTvs);
        });
        AppContext.eventBus.register(
            UpdateRegulationOutlookSelection.NAME,
            (event: UpdateRegulationOutlookSelection) => {
                const selectedRegOutlooks = event.action.replace
                    ? event.selection
                    : Utils.copyAndAddAllIfNeeded(this.state.selectedRegOutlooks, event.selection);
                this.handleRegOutlookSelectionChange(selectedRegOutlooks);
            },
        );
        AppContext.eventBus.register(UpdateTvSelection.NAME, (event: UpdateTvSelection) => {
            if (Utils.isEmpty(event.selection) && event.action === 'add') return;

            let selectedTvs = [];
            if (Utils.isNotEmpty(event.selection)) {
                // event selection can be either a tvId or an ExtendedMonitoredTv
                const selection = Utils.isString(event.selection[0])
                    ? selectDashboardData(store.getState())?.monitoredTvList.filter(tv =>
                          event.selection.includes(tv.refLocation.id),
                      )
                    : (event.selection as ExtendedMonitoredTv[]);
                selectedTvs =
                    event.action === 'replace'
                        ? selection
                        : Utils.copyAndAddAllIfNeeded(this.state.selectedTvs, selection);
            }
            this.handleTVSelectionChange(selectedTvs);
        });
        AppContext.eventBus.register(MapElementClickEvent.NAME, (event: MapElementClickEvent) => {
            if (event.rightClick) {
                //only interested in the right click
                //tvs
                const tfvSelected = event.selection
                    .filter(element => element.type === MapObjectType.TV)
                    .map(mse => mse.subject);
                const dashboardData = selectDashboardData(store.getState());
                const extendedMonitoredTvs =
                    tfvSelected?.length > 0
                        ? dashboardData?.monitoredTvList.filter(tv => tfvSelected.includes(tv.refLocation.id))
                        : [];
                const extendedMonitoredTvIds =
                    tfvSelected?.length > 0
                        ? dashboardData?.monitoredTvList
                              .filter(tv => tfvSelected.includes(tv.refLocation.id))
                              .map(tv => tv.refLocation.id)
                        : [];

                //TODO remove once dashboard data reply has all tvs (including not active ones)
                //tvs from workspace might be ignored (not active and so not in the dashboard.monitoredTvList data)
                //so lets adding them so the right click still shows it but without load graph
                const workspaceTvs = event.selection.filter(
                    element =>
                        element.type === MapObjectType.TV &&
                        element.layerId === MapUtils.WORKSPACE_MAP_LAYER &&
                        !Utils.contains(extendedMonitoredTvIds, element.subject),
                );
                const workspaceExtendedMonitoredTvs = workspaceTvs.map(element => ({
                    refLocation: {
                        type: MonitoredLocationType.TV,
                        id: element.subject,
                    },
                }));
                if (workspaceExtendedMonitoredTvs?.length > 0) {
                    console.warn(
                        'Adding tvs to context menu from workspace layer. Not in the monitored data',
                        workspaceExtendedMonitoredTvs,
                    );
                    extendedMonitoredTvs.push(...workspaceExtendedMonitoredTvs);
                }

                //measures
                const measuresSelected = event.selection
                    .filter(element => element.type === MapObjectType.MEASURE)
                    .map(mse => mse.subject);
                const measures: Array<MonitoredMeasure> =
                    measuresSelected?.length > 0
                        ? dashboardData.monitoredMeasures.filter(measure =>
                              measuresSelected.includes(measure.measure.measureId.id),
                          )
                        : [];

                // hotspots
                const hotspotTvSelected = event.selection
                    .filter(element => element.type === MapObjectType.HOTSPOT)
                    .map(el => el.subject);
                const hotspotsSelected: {
                    [p: string]: HotspotDto[];
                } =
                    hotspotTvSelected?.length > 0
                        ? Object.fromEntries(
                              Object.entries(dashboardData?.hotspots).filter(([tv, _]) =>
                                  hotspotTvSelected.includes(tv),
                              ),
                          )
                        : {};

                const aerodromeIds = event.selection
                    .filter(element => element.type === MapObjectType.REGULATION_OUTLOOK)
                    .map(el => el.subject);
                const regOutlooks = dashboardData?.monitoredRegulationOutlook?.filter(monitoredRegOutlook =>
                    aerodromeIds?.includes(monitoredRegOutlook.regulationOutlook.aerodromeId),
                );

                const workspaceMeasures: Array<MonitoredMeasure> = event.selection
                    .filter(
                        element =>
                            element.type === MapObjectType.MEASURE && element.layerId === MapUtils.WORKSPACE_MAP_LAYER,
                    )
                    ?.map(mce => measures?.find(m => m.measure.measureId.id === mce.subject));

                //convection
                const mapConvectionElements = event.selection.filter(
                    element => element.type === MapObjectType.CONVECTION,
                );
                const convectionData = selectConvectionData(store.getState());
                const tvMeteoForecast: TrafficVolumeMeteoForecast[] = mapConvectionElements
                    .map(element =>
                        WeatherLayerUtils.findTrafficVolumeMeteoForecast(
                            convectionData,
                            element.properties[VALIDITY_PERIOD_PROPERTY_NAME],
                            element.subject,
                        ),
                    )
                    .filter(obj => Utils.isExists(obj));
                const polygonMeteoForecast: PolygonMeteoForecast[] = mapConvectionElements
                    .map(element => {
                        if (Utils.isNotExists(element.properties[TV_PROPERTY_NAME])) {
                            return WeatherLayerUtils.findPolygonMeteoForecast(convectionData, element.subject);
                        } else {
                            const selectedConvection: PolygonMeteoForecast = WeatherLayerUtils.findPolygonMeteoForecast(
                                convectionData,
                                element.subject,
                            );
                            const tv: string = element.properties[TV_PROPERTY_NAME];
                            // Click on a TV inside a polygon convection, to display the info about this TV only, we filter out the impactedTvs
                            const tvAsp = selectedConvection.impactedTvs[tv];
                            if (tvAsp)
                                return Object.assign({}, selectedConvection, {
                                    impactedTvs: { [tv]: selectedConvection.impactedTvs[tv] },
                                });
                            else console.warn('missing tv airspace ref', tv, selectedConvection);
                            return null;
                        }
                    })
                    .filter(obj => Utils.isExists(obj)); //subject is unique
                //military zones
                const mapMilitaryZonesElements = event.selection.filter(
                    element => element.type === MapObjectType.MILITARY,
                );
                const militaryZones: MilitaryZoneDto[] = mapMilitaryZonesElements.map(element =>
                    MapLayerUtils.MILITARY_ZONES_CACHE.find(mz => mz.airspaceId === element.subject),
                );

                //tv impacted
                const tvImpactedElements = event.selection.filter(
                    element => element.type === MapObjectType.IMPACTED_TV,
                );
                const tvImpacts: SolutionTvImpact[] = [];
                for (const tvImpactedElement of tvImpactedElements) {
                    const parsedSubject = TvImpactLayerUtils.parseSubject(tvImpactedElement.subject);
                    if (parsedSubject.solutionName) {
                        const solution = this.props.detailedSolutions.find(
                            sol => sol.name === parsedSubject.solutionName,
                        );
                        const solutionTvImpact = solution.solutionImpact.tvImpact.find(
                            tvImpact =>
                                tvImpact.tvId === parsedSubject.tvId && tvImpact.type === parsedSubject.impactType,
                        );
                        if (solutionTvImpact) {
                            tvImpacts.push({ ...solutionTvImpact, solutionName: solution.name });
                        }
                    }
                }

                //measure impacted
                const measureImpactedElements = event.selection.filter(
                    element => element.type === MapObjectType.IMPACTED_MEASURE,
                );
                const measureImpacts: NiaMeasureImpact[] = [];
                for (const measureImpactedElement of measureImpactedElements) {
                    const parsedSubject = MeasureImpactLayerUtils.parseSubject(measureImpactedElement.subject);
                    if (parsedSubject) {
                        const solution = this.props.detailedSolutions.find(
                            sol => sol.name === parsedSubject.solutionName,
                        );
                        const measureImpact = solution.solutionImpact.measureImpacts[parsedSubject.regulationId];
                        if (measureImpact) {
                            measureImpacts.push(measureImpact);
                        }
                    }
                }
                //show menu
                this.handleMapContextMenuSelection(
                    event.clientXY,
                    extendedMonitoredTvs,
                    measures,
                    hotspotsSelected,
                    tvMeteoForecast,
                    militaryZones,
                    polygonMeteoForecast,
                    workspaceExtendedMonitoredTvs,
                    workspaceMeasures,
                    tvImpacts,
                    measureImpacts,
                    regOutlooks,
                );
            }
        });
        // TODO use PartialDashboardDataUpdateEvent bellow
        AppContext.eventBus.register(CoordinationSummaryUpdateEvent.NAME, (event: CoordinationSummaryUpdateEvent) => {
            const dashboardData = selectDashboardData(store.getState());
            if (Utils.isNotExists(dashboardData?.coordinationSummaries)) return;

            let newSummaries;
            if (dashboardData?.coordinationSummaries.find(c => c.id === event.coordinationSummary.id)) {
                newSummaries = dashboardData?.coordinationSummaries.map(c =>
                    c.id === event.coordinationSummary.id ? event.coordinationSummary : c,
                );
            } else {
                newSummaries = [...dashboardData?.coordinationSummaries, event.coordinationSummary];
            }
            this.props.setPartialDashboardData({ coordinationSummaries: newSummaries });
        });
        AppContext.eventBus.register(PartialDashboardDataUpdateEvent.NAME, (event: PartialDashboardDataUpdateEvent) => {
            if (event.entityType === EntityType.MEASURE_PROPOSAL) {
                ServiceFactory.sol49Service()
                    .getMonitoredMcdmMeasures(selectDashboardData(store.getState())?.period)
                    .then(reply =>
                        this.props.setPartialDashboardData({
                            monitoredMcdmMeasures: this.filteredMcdmMeasures(reply.data),
                        }),
                    )
                    .catch(reason => console.error('getMonitoredMcdmMeasures failed:', reason.message));
            } else if (event.entityType === EntityType.MONITORED_EVENT) {
                this.refreshMonitoredEvents();
            }
        });
        AppContext.eventBus.register(ShowTvDetailEvent.NAME, ({ tv }: ShowTvDetailEvent) => {
            const data = selectDashboardData(store.getState()) as NetworkDashboardData;
            const vsName = data?.tvSetMap?.[tv];
            const monitoredMeasuresForTv = data.monitoredMeasures?.filter(
                monitoredMeasure => MeasureUtils.getTvLocation(monitoredMeasure.measure).id === tv,
            );
            const regulationPeriods = Utils.isNotEmpty(monitoredMeasuresForTv)
                ? monitoredMeasuresForTv.map(monitoredMeasure =>
                      MeasureUtils.getRegulationPeriods(monitoredMeasure.measure),
                  )
                : null;
            ModalHandler.showTvDetails(tv, vsName, regulationPeriods, this.state.selectedFlightsOccupancyPeriods?.[tv]);
        });
        AppContext.eventBus.register(OpenTvMonitoredEvents.NAME, (openEvent: OpenTvMonitoredEvents) => {
            const dashboardData = selectDashboardData(store.getState());
            const filteredEvents = this.state.monitoredEvents.filter(event => {
                if (MonitoredEventUtils.isNoteEvent(event)) {
                    return (
                        event.tvs.includes(openEvent.tvId) ||
                        openEvent.measureIds?.some(id => event.measures.includes(id))
                    );
                } else if (MonitoredEventUtils.isWeatherEvent(event)) {
                    return event.refLocs.map(refLoc => refLoc.refId).includes(openEvent.refLoc?.refId);
                }
                return true;
            });
            const defaultFilters = MonitoredEventUtils.getFiltersOptions(
                this.state.monitoredEvents?.filter(MonitoredEventUtils.isWeatherEvent),
            );
            const content = (
                <MonitoredEventPanelWrapper
                    monitoredEvents={this.state.monitoredEvents}
                    measures={dashboardData?.monitoredMeasures}
                    defaultMeasures={openEvent.measureIds}
                    tvs={dashboardData?.monitoredTvList}
                    defaultTv={openEvent.tvId}
                    fmps={new Set(Object.values((dashboardData as NetworkDashboardData)?.tvSetMap))}
                    defaultFmp={openEvent.tvSet}
                    refLocs={this.sortedRefLocs(dashboardData?.monitoredTvList)}
                    defaultRefLoc={openEvent.refLoc}
                    defaultType={openEvent.eventType}
                    selectedEvent={
                        filteredEvents?.find(e => MonitoredEventUtils.getType(e) === openEvent.eventType)?.id
                    }
                    settings={DEFAULT_MONITORED_EVENTS_SETTINGS}
                    filters={{ types: defaultFilters, options: defaultFilters }}
                />
            );

            ShowModalEvent.fireNotClosable(content, MonitoredEventPanelWrapper.WIDTH);
        });
    };

    initialise = () => {
        if (this.props.configData) {
            this.refreshMonitoredEvents();

            this.props.getDataVersion();

            if (
                new URLSearchParams(window.location.search).get('dataset') === 'static' ||
                new URLSearchParams(window.location.search).get('record-data') === '1'
            ) {
                return; // This is just noise when using static data or recording.
            }

            setInterval(() => {
                // Check if new data is available every 10s
                if (!this.manualUpdate) this.props.getDataVersion();
            }, 10000);
        }
    };

    private updateHighlightedSegments(
        occupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> },
        monitoredTvs?: ExtendedMonitoredTv[],
    ) {
        if (!monitoredTvs) {
            const dashboardData = selectDashboardData(store.getState());
            if (Utils.isExists(dashboardData)) {
                monitoredTvs = this.getMonitoredTvList(dashboardData);
            }
        }

        this.highlightedSegments = this.flightUtils.getHighlightedSegments(occupancyPeriods, monitoredTvs);
    }

    private sendFlights(flights?: FlightDto[]) {
        void this.nmui4dContext?.unityRequests.sendFlightsToUnityAsync(
            flights ?? this.state.selectedFlights,
            this.flightUtils.mergeMap(this.highlightedSegments, this.mapFlightDataWithTvLoads),
        );
    }

    private updateMapFlightData() {
        this.mapFlightDataWithTvLoads = this.flightUtils.computeMapFlightDataWithTvLoads(
            this.enrichedFlights,
            this.display,
        );

        this.sendFlights(this.enrichedFlights);
    }

    private async enrichFlightsAsync(flights: FlightDto[]) {
        const dashboardData = selectDashboardData(store.getState());
        this.enrichedFlights = await this.nmui4dContext?.flightEnricher.enrichAsync(flights, dashboardData?.version);
    }

    private updateFlights(
        flights: FlightDto[],
        occupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> },
        monitoredTvs?: ExtendedMonitoredTv[],
    ) {
        this.updateHighlightedSegments(occupancyPeriods, monitoredTvs);

        this.enrichFlightsAsync(flights)
            .catch(reason => {
                this.nmui4dContext?.logger.error('Failed to retrieve flight data', reason);
                notification.error({ message: 'Error', description: 'Failed to retrieve flight data', duration: 0 });
            })
            .finally(() => {
                this.updateMapFlightData();
            });
    }

    private onDisplayChanged = (newDisplay: CountType) => {
        this.display = newDisplay;
        this.updateMapFlightData();
    };

    private updateConvections(mapSettings?: MapSettings) {
        if (!mapSettings) {
            mapSettings = this.props.mapSettings;
        }

        if (mapSettings?.showConvection) {
            const convectionData = selectConvectionData(store.getState());
            if (
                this.convectionCacheKey &&
                this.convectionCacheKey.networkForecastLastUpdate === convectionData?.networkForecast?.lastUpdate &&
                this.convectionCacheKey.flightLevelFilter === this.flightLevelFilter &&
                this.convectionCacheKey.mapRefTimePeriod === this.mapRefTimePeriod &&
                this.convectionCacheKey.mapSettings === mapSettings
            ) {
                // NOTE(bjarne): We already sent these convections.
                return;
            }

            this.convectionCacheKey = {
                networkForecastLastUpdate: convectionData?.networkForecast?.lastUpdate,
                flightLevelFilter: this.flightLevelFilter,
                mapRefTimePeriod: this.mapRefTimePeriod,
                mapSettings: mapSettings,
            };

            const convectionMap = MapUtils.getConvectionMap(
                convectionData?.networkForecast,
                convectionData?.airspaceDefinition,
                this.mapRefTimePeriod,
            );
            this.nmui4dContext?.unityRequests.sendConvectionsToUnity(
                convectionMap ?? [],
                mapSettings?.showConvectionTvs,
                this.flightLevelFilter,
                this.mapRefTimePeriod,
                mapSettings?.hideConvectionWithNoImpact,
            );
        } else {
            if (this.convectionCacheKey === undefined) {
                // NOTE(bjarne): We already sent these (empty) convections.
                return;
            }

            this.convectionCacheKey = undefined;

            this.nmui4dContext?.unityRequests.sendConvectionsToUnity(
                [],
                mapSettings?.showConvectionTvs,
                this.flightLevelFilter,
                this.mapRefTimePeriod,
                mapSettings?.hideConvectionWithNoImpact,
            );
        }
    }

    private onFlightLevelFilterChanged = (flRange: FlightLevelBand) => {
        this.flightLevelFilter = flRange;
        this.updateConvections();
    };

    private onMapRefTimePeriodChanged = (newPeriod: TimePeriod) => {
        this.mapRefTimePeriod = newPeriod;
        this.updateConvections();
    };

    private refreshMonitoredEvents = () => {
        ServiceFactory.sol49Service()
            .getMonitoredEvents({})
            .then(reply => this.setState({ monitoredEvents: reply.data }))
            .catch(reason => console.error('getMonitoredEvents failed:', reason));
    };

    getMonitoredTvList = memoizeOne((dashboardData: NetworkDashboardData) => dashboardData?.monitoredTvList);

    refreshDashboardData = (callback?: () => void) => {
        const request: DashboardDataRequest = {
            overlappingFilter: this.state.globalFilterEnabled ? this.getOverlappingFilter() : undefined,
            capacityModel: AppContext.getCapacityModel(),
            convectionSource: AppContext.getConvectionSource(),
            monitoredPeriod: AppContext.getMonitoringPeriod(),
            //if show map convection then include the map convection request to be returned
            mapConvectionRequest:
                this.props.loadConvection > 0 || (this.state.showMap && this.props.mapSettings.showConvection)
                    ? { minLevel: this.props.mapSettings.convectionRiskLevel }
                    : undefined,
        };
        const filteredData = this.state?.filteredData;
        this.props
            .getDashboardDataForCurrentVersion(request)
            .then((reply: PayloadAction<Reply<DashboardDataReply>>) => {
                if (reply.payload.error) {
                    console.log('Error while refreshing dashboard data: ', reply.payload.error);
                    notification.error({ message: 'Error', description: reply.payload.error.message, duration: 0 });
                    return;
                }

                const data = reply.payload.data;
                if (filteredData !== this.state?.filteredData) {
                    console.log('Filtered data has changed since the request was sent');
                    data.filteredData = this.state?.filteredData;
                }

                const displayedFlights = this.getDisplayedFlights(data.dashboardData?.flights, data.filteredData);
                const selectedFlights = TableUtils.getUpdatedMultipleSelection(
                    this.state.selectedFlights,
                    displayedFlights,
                    KeyUtils.FLIGHT_KEY_PROVIDER,
                );

                this.updateFlights(
                    selectedFlights,
                    this.state.selectedFlightsOccupancyPeriods,
                    this.getMonitoredTvList(data.dashboardData),
                );
                this.updateConvections();

                // TODO: Adapt filters in regard of updated selection (If selected elements are no longer part of the data, some filters should be removed)
                this.props.setCurrentTime(AppContext.getCurrentTime());
                this.setState({
                    currentTime: AppContext.getCurrentTime(), // Avoids double render, when using current time from props
                    displayedFlights: displayedFlights,
                    filteredData: data.filteredData,
                    selectedTvs: TableUtils.getUpdatedMultipleSelection(
                        this.state.selectedTvs,
                        this.getMonitoredTvList(data.dashboardData),
                        KeyUtils.MONITORED_TV_KEY_PROVIDER,
                    ),
                    selectedMeasures: TableUtils.getUpdatedMultipleSelection(
                        this.state.selectedMeasures,
                        data.dashboardData?.monitoredMeasures,
                        KeyUtils.MONITORED_MEASURE_KEY_PROVIDER,
                    ),
                    selectedFlights: selectedFlights,
                    selectedHotspotTvs: TableUtils.getUpdatedMultipleSelection(
                        this.state.selectedHotspotTvs,
                        this.getMonitoredTvList(data.dashboardData),
                        KeyUtils.MONITORED_TV_KEY_PROVIDER,
                    ),
                    selectedRegOutlooks: TableUtils.getUpdatedMultipleSelection(
                        this.state.selectedRegOutlooks,
                        data.dashboardData?.monitoredRegulationOutlook,
                        KeyUtils.MONITORED_REG_OUTLOOK_KEY_PROVIDER,
                    ),
                    selectedEvents: TableUtils.getUpdatedMultipleSelection(
                        this.state.selectedEvents,
                        this.state.monitoredEvents,
                        KeyUtils.MONITORED_EVENT_KEY_PROVIDER,
                    ),
                });
            })
            .catch(reason => {
                console.error('Failed refreshing dashboard data: ', reason);
            })
            .finally(() => {
                callback?.();
            });
    };

    computeSelectedFlightsOccupancyPeriods = (flightsExtraData: Array<FilteredFlightExtraData>) => {
        let occupancyPeriods: { [key: string]: Map<FlightId, TimePeriod> };
        if (this.state?.selectedFlights) {
            const selectedFlightsIds = this.state?.selectedFlights.map(flight => flight.flightId);
            if (selectedFlightsIds?.length && flightsExtraData) {
                occupancyPeriods = {};
                flightsExtraData.forEach(extraData => {
                    // Ensure that returned occupancies only concern selected flights, as the extraData is also used for filtered flights
                    if (Utils.contains(selectedFlightsIds, extraData?.flightId))
                        for (const tvId in extraData.tvsOccupancyPeriod) {
                            if (!occupancyPeriods[tvId]) {
                                occupancyPeriods[tvId] = new Map<FlightId, TimePeriod>();
                            }
                            occupancyPeriods[tvId].set(extraData.flightId, extraData.tvsOccupancyPeriod[tvId]);
                        }
                });
            }
        }
        return occupancyPeriods;
    };

    refreshFilteredData = () => {
        if (!this.hasFilterEnabled() || !this.state.globalFilterEnabled) {
            // No need to query for selected data - remove filtered data
            const selectedFlights = TableUtils.getUpdatedMultipleSelection(
                this.state.selectedFlights,
                selectDashboardData(store.getState())?.flights,
                KeyUtils.FLIGHT_KEY_PROVIDER,
            );
            this.setState({
                displayedFlights: selectDashboardData(store.getState())?.flights,
                selectedFlights: selectedFlights,
                filteredData: undefined,
                selectedFlightsOccupancyPeriods: undefined,
            });
            this.updateFlights(selectedFlights);
            return;
        }

        // We only refresh filtered data when there is a filter, and it is enabled
        const request: DashboardDataRequest = {
            version: this.props.dataVersion,
            monitoredPeriod: AppContext.getMonitoringPeriod(),
            overlappingFilter: this.getOverlappingFilter(),
            capacityModel: AppContext.getCapacityModel(),
            convectionSource: AppContext.getConvectionSource(),
        };

        this.props
            .getDashboardFilteredData(request)
            .then((reply: PayloadAction<Reply<DashboardFilteredData>>) => {
                const filteredData = reply.payload.data;

                const displayedFlights = this.getDisplayedFlights(
                    selectDashboardData(store.getState())?.flights,
                    filteredData,
                );
                const selectedFlights = TableUtils.getUpdatedMultipleSelection(
                    this.state.selectedFlights,
                    displayedFlights,
                    KeyUtils.FLIGHT_KEY_PROVIDER,
                );
                const occupancyPeriods = this.computeSelectedFlightsOccupancyPeriods(filteredData?.flightsExtraData);
                this.setState({
                    displayedFlights: displayedFlights,
                    selectedFlights: selectedFlights,
                    filteredData: filteredData,
                    selectedFlightsOccupancyPeriods: occupancyPeriods,
                });

                this.updateFlights(selectedFlights, occupancyPeriods);
            })
            .catch(reason => {
                console.log(reason);
                //AppContext.errorNotifier.notifyCatch(reason.message)
            });
    };

    getDisplayedFlights = memoizeOne((flights: FlightDto[], filteredData: DashboardFilteredData) => {
        if (!flights) return [];

        // Create a copy since we're going to exten the flight object, which is not extensible (part of redux store)
        const flightsCopy = Utils.isNotEmpty(filteredData?.flightsExtraData)
            ? flights.map(f => Object.assign({}, f))
            : flights;

        // Filter out flights from the list of filtered flight ids that have been returned
        const displayedFlights = filteredData?.filteredFlights
            ? FlightUtils.getFlights(flightsCopy, filteredData?.filteredFlights)
            : flightsCopy;

        // Add extra data that is specific to filters applied, to the flights
        filteredData?.flightsExtraData?.forEach(flightExtraData => {
            const flight = KeyUtils.getValueFromKey(
                displayedFlights,
                flightExtraData.flightId,
                KeyUtils.FLIGHT_KEY_PROVIDER,
            );
            if (flight) {
                //editing the object directly, the reason for the copy above
                flight.timeAtReferenceLocationEntry = flightExtraData.timeAtReferenceLocationEntry;
                flight.timeAtReferenceLocationExit = flightExtraData.timeAtReferenceLocationExit;
            } else {
                console.log('could not find flight in the data with the following id', flightExtraData.flightId);
            }
        });

        return displayedFlights;
    });

    getOverlappingFilter = (): OverlappingFilter => {
        const filterButtonDetails = [
            this.state.tvMeasureFilterButtonDetails,
            this.state.tvFlightsFilterButtonDetails,
            this.state.measureFlightsFilterButtonDetails,
            this.state.eventTvFilterButtonDetails,
        ];
        return {
            selectedTvs: this.state.selectedTvs.map(monitoredTv =>
                KeyUtils.MONITORED_TV_KEY_PROVIDER.getKey(monitoredTv),
            ),
            selectedMeasures: this.state.selectedMeasures.map(monitoredMeasure =>
                KeyUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(monitoredMeasure),
            ),
            selectedFlights: this.state.selectedFlights.map(flight => KeyUtils.FLIGHT_KEY_PROVIDER.getKey(flight)),
            selectedHotspotTvs: this.state.selectedHotspotTvs.map(monitoredTv =>
                KeyUtils.MONITORED_TV_KEY_PROVIDER.getKey(monitoredTv),
            ),
            selectedRegOutlookTvs: this.state.selectedRegOutlooks.flatMap(mrg => mrg.tvs),
            selectedEvents: this.state.selectedEvents.map(event => KeyUtils.MONITORED_EVENT_KEY_PROVIDER.getKey(event)),
            tvsFilters: OverlappingFilterUtils.getOverlappingFilterForEntity(filterButtonDetails, Entity.TV),
            measuresFilters: OverlappingFilterUtils.getOverlappingFilterForEntity(filterButtonDetails, Entity.MEASURE),
            flightsFilters: OverlappingFilterUtils.getOverlappingFilterForEntity(filterButtonDetails, Entity.FLIGHT),
            eventsFilters: OverlappingFilterUtils.getOverlappingFilterForEntity(filterButtonDetails, Entity.EVENT),
        };
    };

    handleMapSettingChange = (mapSettings: MapSettings) => {
        const shouldRefresh = !this.props.mapSettings.showConvection && mapSettings.showConvection;
        this.props.setMapSettings(mapSettings);
        if (!mapSettings.showConvection) {
            this.props.setPartialDashboardData({ convectionData: null });
        }
        if (shouldRefresh) {
            //the perfect implementation would just force this refresh if the show convection have been changed (false -> true)
            this.refreshDashboardData();
        } else {
            this.updateConvections(mapSettings);
        }
    };
    handleShowMapChange = showMap => {
        const shouldRefresh = !this.state.showMap && showMap;
        this.setState({ showMap: showMap }, () => shouldRefresh && this.refreshDashboardData());
    };
    handleFullscreenMapChange = fullscreenMap => {
        this.setState({ fullscreenMap: fullscreenMap });
    };

    handleTextFilterChange = (textFilter: string): void => {
        this.props.setTextFilter(textFilter); // Update global state
    };

    handleEventSelectionChange = (selectedEvents: Array<MonitoredEvent>) => {
        const wasSelected = Utils.isNotEmpty(this.state.selectedEvents);
        const isSelected = Utils.isNotEmpty(selectedEvents);

        let eventTvsFilterButtonDetails = this.state.eventTvFilterButtonDetails;
        let tvMeasuresFilterButtonDetails = this.state.tvMeasureFilterButtonDetails;

        if (!wasSelected && isSelected) {
            eventTvsFilterButtonDetails = eventTvsFilterButtonDetails.copy({
                enabled: true,
                direction: eventTvsFilterButtonDetails.direction || FilterButtonDirection.FORWARD,
            });
            tvMeasuresFilterButtonDetails = tvMeasuresFilterButtonDetails.copy({
                enabled: true,
                direction: tvMeasuresFilterButtonDetails.direction || FilterButtonDirection.FORWARD,
                forwardEntity: Entity.EVENT, // Force forward entity to "EVENT", making sure to reset it to its default value TV afterwards
            });
        } else if (wasSelected && !isSelected) {
            const hasSelectedTvs = Utils.isNotEmpty(this.state.selectedTvs);

            eventTvsFilterButtonDetails = eventTvsFilterButtonDetails.copy({
                enabled: hasSelectedTvs ? eventTvsFilterButtonDetails.enabled : undefined,
                direction: hasSelectedTvs ? FilterButtonDirection.BACKWARD : FilterButtonDirection.NONE,
            });

            const hasSelectedMeasures = Utils.isNotEmpty(this.state.selectedMeasures);
            tvMeasuresFilterButtonDetails = tvMeasuresFilterButtonDetails.copy({
                enabled: hasSelectedMeasures ? tvMeasuresFilterButtonDetails.enabled : undefined,
                direction: hasSelectedMeasures ? FilterButtonDirection.BACKWARD : FilterButtonDirection.NONE,
                forwardEntity: Entity.TV, // Reset default forward ENTITY
            });
        }

        this.setState(
            {
                selectedEvents: TableUtils.getUpdatedMultipleSelection(
                    selectedEvents,
                    this.state.monitoredEvents,
                    KeyUtils.MONITORED_EVENT_KEY_PROVIDER,
                ),
                eventTvFilterButtonDetails: eventTvsFilterButtonDetails,
                tvMeasureFilterButtonDetails: tvMeasuresFilterButtonDetails,
            },
            () => this.refreshFilteredData(),
        );
    };

    handleTVSelectionChange = (selectedTvs: Array<ExtendedMonitoredTv>) => {
        const wasSelected = Utils.isNotEmpty(this.state.selectedTvs);
        const isSelected = Utils.isNotEmpty(selectedTvs);

        let tvMeasureFilterButtonDetails = this.state.tvMeasureFilterButtonDetails;
        let tvFlightsFilterButtonDetails = this.state.tvFlightsFilterButtonDetails;
        let eventTvFilterButtonDetails = this.state.eventTvFilterButtonDetails;

        if (!wasSelected && isSelected) {
            tvMeasureFilterButtonDetails = tvMeasureFilterButtonDetails.copy({
                enabled: true,
                direction: tvMeasureFilterButtonDetails.direction || FilterButtonDirection.FORWARD,
                forwardEntity: Entity.TV, // reset default filtering entity (see handleEventSettingsChange)
            });
            tvFlightsFilterButtonDetails = tvFlightsFilterButtonDetails.copy({
                enabled: true,
                direction: tvFlightsFilterButtonDetails.direction || FilterButtonDirection.FORWARD,
            });
            eventTvFilterButtonDetails = eventTvFilterButtonDetails.copy({
                enabled: true,
                direction: FilterButtonDirection.BACKWARD,
            });
        } else if (wasSelected && !isSelected) {
            const hasSelectedMeasures =
                Utils.isNotEmpty(this.state.selectedMeasures) ||
                Utils.isNotEmpty(this.state.selectedHotspotTvs) ||
                Utils.isNotEmpty(this.state.selectedRegOutlooks);
            const hasSelectedFlights = Utils.isNotEmpty(this.state.selectedFlights);
            const hasSelectedEvents = Utils.isNotEmpty(this.state.selectedEvents);

            tvMeasureFilterButtonDetails = tvMeasureFilterButtonDetails.copy({
                enabled: hasSelectedMeasures ? tvMeasureFilterButtonDetails.enabled : undefined,
                direction: hasSelectedMeasures ? FilterButtonDirection.BACKWARD : FilterButtonDirection.NONE,
            });
            tvFlightsFilterButtonDetails = tvFlightsFilterButtonDetails.copy({
                enabled: hasSelectedFlights ? tvFlightsFilterButtonDetails.enabled : undefined,
                direction: hasSelectedFlights ? FilterButtonDirection.BACKWARD : FilterButtonDirection.NONE,
            });
            eventTvFilterButtonDetails = eventTvFilterButtonDetails.copy({
                enabled: hasSelectedEvents ? eventTvFilterButtonDetails.enabled : undefined,
                direction: hasSelectedEvents ? FilterButtonDirection.FORWARD : FilterButtonDirection.NONE,
            });
        }

        const updatedSelectedTvs = TableUtils.getUpdatedMultipleSelection(
            selectedTvs,
            this.getMonitoredTvList(selectDashboardData(store.getState())),
            KeyUtils.MONITORED_TV_KEY_PROVIDER,
        );

        this.setState(
            {
                selectedTvs: updatedSelectedTvs,
                tvMeasureFilterButtonDetails: tvMeasureFilterButtonDetails,
                tvFlightsFilterButtonDetails: tvFlightsFilterButtonDetails,
                eventTvFilterButtonDetails: eventTvFilterButtonDetails,
            },
            () => this.refreshFilteredData(),
        );

        void this.nmui4dContext?.unityRequests.sendTVsToUnityAsync(updatedSelectedTvs);
    };

    handleHotspotSelectionChange = (selectedHotspots: Array<ExtendedMonitoredTv>) => {
        const wasSelected =
            Utils.isNotEmpty(this.state.selectedHotspotTvs) ||
            Utils.isNotEmpty(this.state.selectedMeasures) ||
            Utils.isNotEmpty(this.state.selectedRegOutlooks);
        const isSelected =
            Utils.isNotEmpty(selectedHotspots) ||
            Utils.isNotEmpty(this.state.selectedMeasures) ||
            Utils.isNotEmpty(this.state.selectedRegOutlooks);

        let tvMeasureFilterButtonDetails = this.state.tvMeasureFilterButtonDetails;
        let measureFlightsFilterButtonDetails = this.state.measureFlightsFilterButtonDetails;

        if (!wasSelected && isSelected) {
            tvMeasureFilterButtonDetails = tvMeasureFilterButtonDetails.copy({
                enabled: true,
                direction: tvMeasureFilterButtonDetails.direction || FilterButtonDirection.BACKWARD,
            });
            measureFlightsFilterButtonDetails = measureFlightsFilterButtonDetails.copy({
                enabled: true,
                direction: measureFlightsFilterButtonDetails.direction || FilterButtonDirection.FORWARD,
            });
        } else if (wasSelected && !isSelected) {
            const hasSelectedTvs = Utils.isNotEmpty(this.state.selectedTvs);
            const hasSelectedFlights = Utils.isNotEmpty(this.state.selectedFlights);

            tvMeasureFilterButtonDetails = tvMeasureFilterButtonDetails.copy({
                enabled: hasSelectedTvs ? tvMeasureFilterButtonDetails.enabled : undefined,
                direction: hasSelectedTvs ? FilterButtonDirection.FORWARD : FilterButtonDirection.NONE,
            });
            measureFlightsFilterButtonDetails = measureFlightsFilterButtonDetails.copy({
                enabled: hasSelectedFlights ? measureFlightsFilterButtonDetails.enabled : undefined,
                direction: hasSelectedFlights ? FilterButtonDirection.BACKWARD : FilterButtonDirection.NONE,
            });
        }

        this.setState(
            {
                selectedHotspotTvs: TableUtils.getUpdatedMultipleSelection(
                    selectedHotspots,
                    selectDashboardData(store.getState()).monitoredTvList,
                    KeyUtils.MONITORED_TV_KEY_PROVIDER,
                ),
                tvMeasureFilterButtonDetails: tvMeasureFilterButtonDetails,
                measureFlightsFilterButtonDetails: measureFlightsFilterButtonDetails,
            },
            () => this.refreshFilteredData(),
        );
    };

    handleRegOutlookSelectionChange = (selectedRegOutlooks: Array<MonitoredRegulationOutlook>) => {
        const wasSelected =
            Utils.isNotEmpty(this.state.selectedRegOutlooks) ||
            Utils.isNotEmpty(this.state.selectedMeasures) ||
            Utils.isNotEmpty(this.state.selectedHotspotTvs);
        const isSelected =
            Utils.isNotEmpty(selectedRegOutlooks) ||
            Utils.isNotEmpty(this.state.selectedMeasures) ||
            Utils.isNotEmpty(this.state.selectedHotspotTvs);

        let tvMeasureFilterButtonDetails = this.state.tvMeasureFilterButtonDetails;
        let measureFlightsFilterButtonDetails = this.state.measureFlightsFilterButtonDetails;

        if (!wasSelected && isSelected) {
            tvMeasureFilterButtonDetails = tvMeasureFilterButtonDetails.copy({
                enabled: true,
                direction: tvMeasureFilterButtonDetails.direction || FilterButtonDirection.BACKWARD,
            });
            measureFlightsFilterButtonDetails = measureFlightsFilterButtonDetails.copy({
                enabled: true,
                direction: measureFlightsFilterButtonDetails.direction || FilterButtonDirection.FORWARD,
            });
        } else if (wasSelected && !isSelected) {
            const hasSelectedTvs = Utils.isNotEmpty(this.state.selectedTvs);
            const hasSelectedFlights = Utils.isNotEmpty(this.state.selectedFlights);

            tvMeasureFilterButtonDetails = tvMeasureFilterButtonDetails.copy({
                enabled: hasSelectedTvs ? tvMeasureFilterButtonDetails.enabled : undefined,
                direction: hasSelectedTvs ? FilterButtonDirection.FORWARD : FilterButtonDirection.NONE,
            });
            measureFlightsFilterButtonDetails = measureFlightsFilterButtonDetails.copy({
                enabled: hasSelectedFlights ? measureFlightsFilterButtonDetails.enabled : undefined,
                direction: hasSelectedFlights ? FilterButtonDirection.BACKWARD : FilterButtonDirection.NONE,
            });
        }

        this.setState(
            {
                selectedRegOutlooks: TableUtils.getUpdatedMultipleSelection(
                    selectedRegOutlooks,
                    selectDashboardData(store.getState()).monitoredRegulationOutlook,
                    KeyUtils.MONITORED_REG_OUTLOOK_KEY_PROVIDER,
                ),
                tvMeasureFilterButtonDetails: tvMeasureFilterButtonDetails,
                measureFlightsFilterButtonDetails: measureFlightsFilterButtonDetails,
            },
            () => this.refreshFilteredData(),
        );
    };

    handleMeasureSelectionChange = (selectedMeasures: Array<MonitoredMeasure>) => {
        const wasSelected =
            Utils.isNotEmpty(this.state.selectedMeasures) ||
            Utils.isNotEmpty(this.state.selectedHotspotTvs) ||
            Utils.isNotEmpty(this.state.selectedRegOutlooks);
        const isSelected =
            Utils.isNotEmpty(selectedMeasures) ||
            Utils.isNotEmpty(this.state.selectedHotspotTvs) ||
            Utils.isNotEmpty(this.state.selectedRegOutlooks);

        let tvMeasureFilterButtonDetails = this.state.tvMeasureFilterButtonDetails;
        let measureFlightsFilterButtonDetails = this.state.measureFlightsFilterButtonDetails;

        if (!wasSelected && isSelected) {
            tvMeasureFilterButtonDetails = tvMeasureFilterButtonDetails.copy({
                enabled: true,
                direction: tvMeasureFilterButtonDetails.direction || FilterButtonDirection.BACKWARD,
            });
            measureFlightsFilterButtonDetails = measureFlightsFilterButtonDetails.copy({
                enabled: true,
                direction: measureFlightsFilterButtonDetails.direction || FilterButtonDirection.FORWARD,
            });
        } else if (wasSelected && !isSelected) {
            const hasSelectedTvs = Utils.isNotEmpty(this.state.selectedTvs);
            const hasSelectedFlights = Utils.isNotEmpty(this.state.selectedFlights);

            tvMeasureFilterButtonDetails = tvMeasureFilterButtonDetails.copy({
                enabled: hasSelectedTvs ? tvMeasureFilterButtonDetails.enabled : undefined,
                direction: hasSelectedTvs ? FilterButtonDirection.FORWARD : FilterButtonDirection.NONE,
            });
            measureFlightsFilterButtonDetails = measureFlightsFilterButtonDetails.copy({
                enabled: hasSelectedFlights ? measureFlightsFilterButtonDetails.enabled : undefined,
                direction: hasSelectedFlights ? FilterButtonDirection.BACKWARD : FilterButtonDirection.NONE,
            });
        }

        this.setState(
            {
                selectedMeasures: TableUtils.getUpdatedMultipleSelection(
                    selectedMeasures,
                    selectDashboardData(store.getState()).monitoredMeasures,
                    KeyUtils.MONITORED_MEASURE_KEY_PROVIDER,
                ),
                tvMeasureFilterButtonDetails: tvMeasureFilterButtonDetails,
                measureFlightsFilterButtonDetails: measureFlightsFilterButtonDetails,
            },
            () => this.refreshFilteredData(),
        );
    };

    handleFlightSelectionChange = (selectedFlights: Array<FlightDto>) => {
        const wasSelected = Utils.isNotEmpty(this.state.selectedFlights);
        const isSelected = Utils.isNotEmpty(selectedFlights);

        let tvFlightsFilterButtonDetails = this.state.tvFlightsFilterButtonDetails;
        let measureFlightsFilterButtonDetails = this.state.measureFlightsFilterButtonDetails;

        if (!wasSelected && isSelected) {
            tvFlightsFilterButtonDetails = tvFlightsFilterButtonDetails.copy({
                enabled: true,
                direction: tvFlightsFilterButtonDetails.direction || FilterButtonDirection.BACKWARD,
            });
            measureFlightsFilterButtonDetails = measureFlightsFilterButtonDetails.copy({
                enabled: true,
                direction: measureFlightsFilterButtonDetails.direction || FilterButtonDirection.BACKWARD,
            });
        } else if (wasSelected && !isSelected) {
            const hasSelectedTvs = Utils.isNotEmpty(this.state.selectedTvs);
            const hasSelectedMeasures =
                Utils.isNotEmpty(this.state.selectedMeasures) ||
                Utils.isNotEmpty(this.state.selectedHotspotTvs) ||
                Utils.isNotEmpty(this.state.selectedRegOutlooks);

            tvFlightsFilterButtonDetails = tvFlightsFilterButtonDetails.copy({
                enabled: hasSelectedTvs ? tvFlightsFilterButtonDetails.enabled : undefined,
                direction: hasSelectedTvs ? FilterButtonDirection.FORWARD : FilterButtonDirection.NONE,
            });
            measureFlightsFilterButtonDetails = measureFlightsFilterButtonDetails.copy({
                enabled: hasSelectedMeasures ? measureFlightsFilterButtonDetails.enabled : undefined,
                direction: hasSelectedMeasures ? FilterButtonDirection.FORWARD : FilterButtonDirection.NONE,
            });
        }

        const updatedSelectedFlights = TableUtils.getUpdatedMultipleSelection(
            selectedFlights,
            this.state.displayedFlights,
            KeyUtils.FLIGHT_KEY_PROVIDER,
        );

        this.setState(
            {
                selectedFlights: updatedSelectedFlights,
                tvFlightsFilterButtonDetails: tvFlightsFilterButtonDetails,
                measureFlightsFilterButtonDetails: measureFlightsFilterButtonDetails,
            },
            () => this.refreshFilteredData(),
        );
    };

    hasFilterEnabled = () => {
        return (
            Utils.isNotEmpty(this.state.selectedTvs) ||
            Utils.isNotEmpty(this.state.selectedMeasures) ||
            Utils.isNotEmpty(this.state.selectedFlights) ||
            Utils.isNotEmpty(this.state.selectedHotspotTvs) ||
            Utils.isNotEmpty(this.state.selectedRegOutlooks) ||
            Utils.isNotEmpty(this.state.selectedEvents) ||
            Utils.isNotShallowEquals(this.state.flightListFilterSettings, DEFAULT_FLIGHT_LIST_FILTER_SETTINGS) ||
            Utils.isNotEmptyString(this.props.globalTextFilter)
        );
    };

    clearFilters = () => {
        this.handleTextFilterChange('');
        this.setState(
            {
                selectedTvs: [],
                selectedMeasures: [],
                selectedHotspotTvs: [],
                selectedRegOutlooks: [],
                selectedFlights: [],
                selectedEvents: [],
                tvMeasureFilterButtonDetails: new OverlappingFilterButtonDetail(
                    Entity.TV,
                    Entity.MEASURE,
                    DEFAULT_TV_MEASURE_SETTINGS,
                    DEFAULT_MEASURE_TV_SETTINGS,
                ),
                tvFlightsFilterButtonDetails: new OverlappingFilterButtonDetail(
                    Entity.TV,
                    Entity.FLIGHT,
                    DEFAULT_TV_FLIGHTS_SETTINGS,
                    DEFAULT_FLIGHTS_TV_SETTINGS,
                ),
                measureFlightsFilterButtonDetails: new OverlappingFilterButtonDetail(
                    Entity.MEASURE,
                    Entity.FLIGHT,
                    DEFAULT_MEASURE_FLIGHTS_SETTINGS,
                    DEFAULT_FLIGHTS_MEASURE_SETTINGS,
                ),
                eventTvFilterButtonDetails: new OverlappingFilterButtonDetail(
                    Entity.EVENT,
                    Entity.TV,
                    DEFAULT_EVENTS_TV_SETTINGS,
                    DEFAULT_TV_EVENTS_SETTINGS,
                ),
                globalFilterEnabled: true, //reset global filtering toggle,
                flightListFilterSettings: DEFAULT_FLIGHT_LIST_FILTER_SETTINGS, // reset flight list filter settings
            },
            () => this.refreshFilteredData(),
        );

        void this.nmui4dContext?.unityRequests.sendTVsToUnityAsync([]);
    };

    handleFlightListFilterSettingsChange = (settings: FlightListFilterSettings) => {
        this.setState({ flightListFilterSettings: settings });
    };

    handleTvFlightsFilterSettingsChange = (settings: TvFlightsFilterSettings) => {
        this.setState(
            {
                tvFlightsFilterButtonDetails: this.state.tvFlightsFilterButtonDetails.copy({
                    forwardSettings: settings,
                }),
            },
            () => this.refreshFilteredData(),
        );
    };
    handleFlightsTvFilterSettingsChange = (settings: FlightsTvFilterSettings) => {
        this.setState(
            {
                tvFlightsFilterButtonDetails: this.state.tvFlightsFilterButtonDetails.copy({
                    backwardSettings: settings,
                }),
            },
            () => this.refreshFilteredData(),
        );
    };
    handleMeasureFlightsFilterSettingsChange = (settings: MeasureFlightsFilterSettings) => {
        this.setState(
            {
                measureFlightsFilterButtonDetails: this.state.measureFlightsFilterButtonDetails.copy({
                    forwardSettings: settings,
                }),
            },
            () => this.refreshFilteredData(),
        );
    };
    handleFlightsMeasureFilterSettingsChange = (settings: MeasureFlightsFilterSettings) => {
        this.setState(
            {
                measureFlightsFilterButtonDetails: this.state.measureFlightsFilterButtonDetails.copy({
                    backwardSettings: settings,
                }),
            },
            () => this.refreshFilteredData(),
        );
    };
    handleTvMeasureFilterSettingsChange = (settings: TvMeasureFilterSettings) => {
        this.setState(
            {
                tvMeasureFilterButtonDetails: this.state.tvMeasureFilterButtonDetails.copy({
                    forwardSettings: settings,
                }),
            },
            () => this.refreshFilteredData(),
        );
    };
    handleMeasureTvFilterSettingsChange = (settings: MeasureTvFilterSettings) => {
        this.setState(
            {
                tvMeasureFilterButtonDetails: this.state.tvMeasureFilterButtonDetails.copy({
                    backwardSettings: settings,
                }),
            },
            () => this.refreshFilteredData(),
        );
    };
    handleEventTvFilterSettingsChange = (settings: EventsTvFilterSettings) => {
        this.setState(
            { eventTvFilterButtonDetails: this.state.eventTvFilterButtonDetails.copy({ forwardSettings: settings }) },
            () => this.refreshFilteredData(),
        );
    };
    handleTvEventFilterSettingsChange = (settings: TvEventsFilterSettings) => {
        this.setState(
            { eventTvFilterButtonDetails: this.state.eventTvFilterButtonDetails.copy({ backwardSettings: settings }) },
            () => this.refreshFilteredData(),
        );
    };
    handleGlobalFilterEnabledChange = (value: boolean) => {
        this.setState({ globalFilterEnabled: value }, () => this.refreshFilteredData());
    };
    handleMapContextMenuSelection = (
        position: number[],
        tvs: Array<ExtendedMonitoredTv>,
        measures: Array<MonitoredMeasure>,
        hotspots: { [p: string]: HotspotDto[] },
        tvConvections: Array<TrafficVolumeMeteoForecast>,
        militaryZones: Array<MilitaryZoneDto>,
        polygonConvection: PolygonMeteoForecast[],
        workspaceTvs: Array<ExtendedMonitoredTv>,
        workspaceMeasures: Array<MonitoredMeasure>,
        tvImpacts: Array<SolutionTvImpact>,
        measureImpacts: Array<NiaMeasureImpact>,
        regOutlooks: Array<MonitoredRegulationOutlook>,
    ): void => {
        const convectionTvNames: Array<string> = [];
        tvConvections && convectionTvNames.push(...tvConvections.map(c => c.id));
        polygonConvection && convectionTvNames.push(...polygonConvection.flatMap(c => Object.keys(c.impactedTvs)));

        const dashboardData = selectDashboardData(store.getState());
        const mapContextMenuProps: MapContextMenuProps = {
            tvs: tvs,
            workspaceTvs: workspaceTvs,
            measures: measures,
            hotspots: hotspots,
            regOutlooks: regOutlooks,
            workspaceMeasures: workspaceMeasures,
            selectedFlightsOccupancyPeriods: this.state.selectedFlightsOccupancyPeriods,
            currentTime: this.state.currentTime,
            selectedTvs: this.state.selectedTvs,
            proposalMeasureIds: MeasureUtils.getProposalMeasureIds(dashboardData?.monitoredMcdmMeasures),
            monitoredMeasures: dashboardData?.monitoredMeasures,
            monitoredTvList: dashboardData?.monitoredTvList,
            tvSetMap: (dashboardData as NetworkDashboardData)?.tvSetMap,
            tvConvections: tvConvections,
            convectionTvs: dashboardData.monitoredTvList.filter(tv => convectionTvNames.includes(tv.refLocation.id)),
            militaryZones: militaryZones,
            polygonConvections: polygonConvection,
            impactedTvs: tvImpacts,
            impactedMeasures: measureImpacts,
            events: this.state.monitoredEvents,
            meteoAlerts: dashboardData.tvMeteoAlerts,
        };
        const modalContent = <MapContextMenu {...mapContextMenuProps} />;
        AppContext.eventBus.publish(
            new ShowModalEvent(
                ShowModalEvent.NAME,
                modalContent,
                MapContextMenu.hasOnlyOneKindOfMenu(mapContextMenuProps)
                    ? MapContextMenu.MAP_CONTEXT_MENU_SINGLE_WIDTH_EM
                    : MapContextMenu.MAP_CONTEXT_MENU_GROUP_WIDTH_EM,
                position[0],
                position[1],
            ),
        );
    };
    handleMeasureSettingsChange = (value: MeasuresSettings) => {
        this.setState({ measureSettings: value });
    };
    handleHotspotSettingsChange = (value: HotspotSettings) => {
        this.setState({ hotspotSettings: value });
    };
    getTvFlightFilter = () => {
        const filterButtonDetails = this.state.tvFlightsFilterButtonDetails;

        if (filterButtonDetails.direction === FilterButtonDirection.NONE) return;

        // TODO: have state of the filter changed to checked:false when settings are applied
        const settingsButton =
            filterButtonDetails.direction === FilterButtonDirection.FORWARD ? (
                <TvFlightsFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleTvFlightsFilterSettingsChange}
                />
            ) : (
                <FlightsTvFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleFlightsTvFilterSettingsChange}
                />
            );

        return (
            <FilterComponent
                enabled={filterButtonDetails.enabled && this.state.globalFilterEnabled}
                orientation={DashboardUtils.getFilterOrientation(filterButtonDetails, 'horizontal')}
                onEnabledChange={enabled =>
                    this.setState(
                        {
                            tvFlightsFilterButtonDetails: DashboardUtils.getButtonDetailsOnEnabledUpdate(
                                filterButtonDetails,
                                enabled,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                onOrientationChange={orientation =>
                    this.setState(
                        {
                            tvFlightsFilterButtonDetails: DashboardUtils.getButtonDetailsOnOrientationUpdate(
                                filterButtonDetails,
                                orientation,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                reverseEnabled={
                    filterButtonDetails.direction === FilterButtonDirection.FORWARD
                        ? Utils.isNotEmpty(this.state.selectedFlights)
                        : Utils.isNotEmpty(this.state.selectedTvs)
                }
                settingsButton={settingsButton}
            />
        );
    };
    getEventTvFilter = () => {
        const filterButtonDetails = this.state.eventTvFilterButtonDetails;
        // TODO: have state of the filter changed to checked:false when settings are applied
        const settingsButton =
            filterButtonDetails.direction === FilterButtonDirection.FORWARD ? (
                <EventsTvFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleEventTvFilterSettingsChange}
                />
            ) : (
                <TvEventsFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleTvEventFilterSettingsChange}
                />
            );

        return (
            <FilterComponent
                enabled={filterButtonDetails.enabled && this.state.globalFilterEnabled}
                orientation={DashboardUtils.getFilterOrientation(filterButtonDetails, 'horizontal')}
                onEnabledChange={enabled =>
                    this.setState(
                        {
                            eventTvFilterButtonDetails: DashboardUtils.getButtonDetailsOnEnabledUpdate(
                                filterButtonDetails,
                                enabled,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                onOrientationChange={orientation =>
                    this.setState(
                        {
                            eventTvFilterButtonDetails: DashboardUtils.getButtonDetailsOnOrientationUpdate(
                                filterButtonDetails,
                                orientation,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                reverseEnabled={
                    filterButtonDetails.direction === FilterButtonDirection.FORWARD
                        ? Utils.isNotEmpty(this.state.selectedTvs)
                        : Utils.isNotEmpty(this.state.selectedEvents)
                }
                settingsButton={settingsButton}
            />
        );
    };
    getTvMeasureFilter = () => {
        const filterButtonDetails = this.state.tvMeasureFilterButtonDetails;

        // TODO: have state of the filter changed to checked:false when settings are applied
        const settingsButton =
            filterButtonDetails.direction === FilterButtonDirection.FORWARD ? (
                <TvMeasureFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleTvMeasureFilterSettingsChange}
                />
            ) : (
                <MeasureTvFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleMeasureTvFilterSettingsChange}
                />
            );

        return (
            <FilterComponent
                enabled={filterButtonDetails.enabled && this.state.globalFilterEnabled}
                orientation={DashboardUtils.getFilterOrientation(filterButtonDetails, 'horizontal')}
                onEnabledChange={enabled =>
                    this.setState(
                        {
                            tvMeasureFilterButtonDetails: DashboardUtils.getButtonDetailsOnEnabledUpdate(
                                filterButtonDetails,
                                enabled,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                onOrientationChange={orientation =>
                    this.setState(
                        {
                            tvMeasureFilterButtonDetails: DashboardUtils.getButtonDetailsOnOrientationUpdate(
                                filterButtonDetails,
                                orientation,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                reverseEnabled={
                    filterButtonDetails.direction === FilterButtonDirection.FORWARD
                        ? Utils.isNotEmpty(this.state.selectedMeasures) ||
                          Utils.isNotEmpty(this.state.selectedHotspotTvs) ||
                          Utils.isNotEmpty(this.state.selectedRegOutlooks)
                        : Utils.isNotEmpty(this.state.selectedTvs)
                }
                settingsButton={settingsButton}
            />
        );
    };
    getMeasureFlightFilter = () => {
        const filterButtonDetails = this.state.measureFlightsFilterButtonDetails;

        // TODO: have state of the filter changed to checked:false when settings are applied
        const settingsButton =
            filterButtonDetails.direction === FilterButtonDirection.FORWARD ? (
                <MeasureFlightsFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleMeasureFlightsFilterSettingsChange}
                />
            ) : (
                <FlightsMeasureFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleFlightsMeasureFilterSettingsChange}
                />
            );
        return (
            <FilterComponent
                enabled={filterButtonDetails.enabled && this.state.globalFilterEnabled}
                orientation={DashboardUtils.getFilterOrientation(filterButtonDetails, 'vertical')}
                onEnabledChange={enabled =>
                    this.setState(
                        {
                            measureFlightsFilterButtonDetails: DashboardUtils.getButtonDetailsOnEnabledUpdate(
                                filterButtonDetails,
                                enabled,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                onOrientationChange={orientation =>
                    this.setState(
                        {
                            measureFlightsFilterButtonDetails: DashboardUtils.getButtonDetailsOnOrientationUpdate(
                                filterButtonDetails,
                                orientation,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                reverseEnabled={
                    filterButtonDetails.direction === FilterButtonDirection.FORWARD
                        ? Utils.isNotEmpty(this.state.selectedFlights)
                        : Utils.isNotEmpty(this.state.selectedMeasures) ||
                          Utils.isNotEmpty(this.state.selectedHotspotTvs) ||
                          Utils.isNotEmpty(this.state.selectedRegOutlooks)
                }
                settingsButton={settingsButton}
            />
        );
    };

    filteredHotspots = () => {
        const hotspots = selectDashboardData(store.getState())?.hotspots;
        if (!hotspots) return;

        return Object.keys(hotspots)
            .filter(tv => Utils.contains(this.state.filteredData?.filteredHotspotTvs, tv))
            .reduce((obj, key) => {
                obj[key] = hotspots[key];
                return obj;
            }, {});
    };

    filteredMcdmMeasures = memoizeOne((monitoredMcdmMeasures: Array<MonitoredMcdmMeasure>) => {
        return monitoredMcdmMeasures?.filter(
            m =>
                !this.monitoredZones ||
                this.monitoredZones.includes(MeasureUtils.getTvLocation(m.measure).refLocation?.refId?.substring(0, 2)),
        );
    });

    filteredEvents = memoizeOne((events: MonitoredEvent[], filteredEvents: string[]) => {
        // Keep all ops Instructions + filtered events
        return events?.filter(
            event => MonitoredEventUtils.isOpsInstruction(event) || filteredEvents?.includes(event.id),
        );
    });

    sortedRefLocs = memoizeOne((tvList: ExtendedMonitoredTv[]) => {
        const sortedUniqueRefLocs = Array.from(
            new Map(tvList?.map(mtv => [mtv.tvRefLocation['refId'], mtv.tvRefLocation])).values(),
        );
        sortedUniqueRefLocs.sort((a, b) => a.refId.localeCompare(b.refId));
        return sortedUniqueRefLocs;
    });

    uniqueTvSets = memoizeOne((tvSetMap: { [tv: string]: string }) => {
        return new Set(Object.values(tvSetMap));
    });

    handleMainFlexMouseMove = event => {
        // if (event.pageX > (window.outerWidth - 10) && !this.props.drawerOpen) {
        if (event.pageX < 10 && !this.props.drawerOpen) {
            this.props.setDrawerOpen(true);
        }
    };
    handleToggleChatPin = () => {
        this.setState(prevState => ({
            chatPin: !prevState.chatPin,
        }));
    };

    handleChatOnMouseLeave = () => {
        if (!this.state.chatPin) {
            this.props.setDrawerOpen(false);
        }
    };

    handleChatMouseMove = () => {};

    render() {
        const dashboardData = selectDashboardData(store.getState());
        if (Utils.isNotExists(dashboardData)) return null;

        // TODO: Any state change triggers the re-rendering of the filter components, which is not optimal (use memoize-one?)
        const tvMeasureFilterForward =
            this.state.tvMeasureFilterButtonDetails.direction === FilterButtonDirection.FORWARD;
        const tvFlightsFilterForward =
            this.state.tvFlightsFilterButtonDetails.direction === FilterButtonDirection.FORWARD;
        const measureFlightsFilterForward =
            this.state.measureFlightsFilterButtonDetails.direction === FilterButtonDirection.FORWARD;

        const tvMeasureFilterVisible = this.state.tvMeasureFilterButtonDetails.direction !== FilterButtonDirection.NONE;
        const nbFiltersOnTv =
            (this.state.tvMeasureFilterButtonDetails.direction === FilterButtonDirection.BACKWARD ? 1 : 0) +
            (this.state.tvFlightsFilterButtonDetails.direction === FilterButtonDirection.BACKWARD ? 1 : 0) +
            (this.state.eventTvFilterButtonDetails.direction === FilterButtonDirection.FORWARD ? 1 : 0);
        const tvFilterApplied =
            nbFiltersOnTv > 0 ? (Utils.isEmpty(this.state.selectedTvs) ? 'secondary-tv' : 'tertiary-tv') : 'primary';

        const measureFlightFilterVisible =
            this.state.measureFlightsFilterButtonDetails.direction !== FilterButtonDirection.NONE;
        const nbFiltersOnMeasures =
            (this.state.tvMeasureFilterButtonDetails.direction === FilterButtonDirection.FORWARD ? 1 : 0) +
            (this.state.measureFlightsFilterButtonDetails.direction === FilterButtonDirection.BACKWARD ? 1 : 0);
        const measureFilterApplied =
            nbFiltersOnMeasures > 0 ? (nbFiltersOnMeasures === 1 ? 'secondary' : 'tertiary') : 'primary';

        const tvFlightFilterVisible = this.state.tvFlightsFilterButtonDetails.direction !== FilterButtonDirection.NONE;
        const nbFiltersOnFlights =
            (this.state.tvFlightsFilterButtonDetails.direction === FilterButtonDirection.FORWARD ? 1 : 0) +
            (this.state.measureFlightsFilterButtonDetails.direction === FilterButtonDirection.FORWARD ? 1 : 0);
        const flightFilterApplied =
            nbFiltersOnFlights > 0 ? (nbFiltersOnFlights === 1 ? 'secondary' : 'tertiary') : 'primary';

        const eventTvFilterForward = this.state.eventTvFilterButtonDetails.direction === FilterButtonDirection.FORWARD;
        const eventTvFilterVisible = this.state.eventTvFilterButtonDetails.direction !== FilterButtonDirection.NONE;
        const eventTvFilterApplied =
            this.state.eventTvFilterButtonDetails.direction === FilterButtonDirection.BACKWARD
                ? 'secondary'
                : 'primary';

        const displayedMeasures = this.state.filteredData?.filteredMeasures
            ? dashboardData?.monitoredMeasures?.filter(monitoredMeasure => {
                  return Utils.contains(this.state.filteredData?.filteredMeasures, monitoredMeasure.measure.measureId);
              })
            : dashboardData?.monitoredMeasures;

        const displayedHotspots = this.state.filteredData?.filteredHotspotTvs
            ? this.filteredHotspots()
            : dashboardData?.hotspots;
        let displayedRegOutlook = dashboardData?.monitoredRegulationOutlook;
        if (this.state.filteredData?.filteredRegulationOutlook) {
            displayedRegOutlook = displayedRegOutlook?.filter(mrg =>
                Utils.contains(this.state.filteredData?.filteredRegulationOutlook, mrg.regulationOutlook.aerodromeId),
            );
        }

        const displayedEvents = this.state.filteredData?.filteredEvents
            ? this.filteredEvents(this.state.monitoredEvents, this.state.filteredData?.filteredEvents)
            : this.state.monitoredEvents;

        // Get the list of selected TV ids from the selected monitored tvs and selected monitored measures
        // Entry and Exit columns in the flight list are only displayed when only one single concerned TV is selected
        const selectedTvs = this.state.selectedTvs.map(monitoredTv => monitoredTv.refLocation.id);
        Utils.addAllIfNeeded(
            selectedTvs,
            this.state.selectedMeasures.map(
                monitoredMeasure => MeasureUtils.getTvLocation(monitoredMeasure.measure).id,
            ),
        );
        const showEntryExitColumns = selectedTvs.length === 1 && nbFiltersOnFlights > 0;

        const globalFilterClassName = !this.state.globalFilterEnabled ? ' global-filter-disabled' : '';

        return (
            <Spin spinning={this.state.loading}>
                <ConditionalRender condition={!AppConstants.hideChatDrawer}>
                    {/*hide the header so the mouse leave on the body doesn't get triggered when on the header*/}
                    <Drawer
                        width="13vw"
                        headerStyle={{ width: 0, display: 'none' }}
                        forceRender={true}
                        open={this.props.drawerOpen}
                        placement={'left'}
                        className={`iflowDrawer left`}
                        mask={false}>
                        <Flex direction="vertical" onMouseLeave={this.handleChatOnMouseLeave} size="13vw">
                            {/*header within the body (to avoid the mouse leave)*/}
                            <Flex direction="vertical" size="2em" style={{ alignItems: 'flex-end', padding: '1em' }}>
                                <span
                                    style={{ fontSize: '1.5em', cursor: 'pointer' }}
                                    onClick={this.handleToggleChatPin}>
                                    {this.state.chatPin ? <PushpinFilled /> : <PushpinOutlined />}
                                </span>
                            </Flex>
                            <ChatPanel
                                userId={AppContext.configData.claimedUserId}
                                pollingIntervalInMs={POLLING_REFRESH_INTERVAL_IN_MS}
                                pausePolling={false}
                                maxNumPollingFailures={MAX_ALLOWED_POLLING_COMMUNICATION_FAILURES}
                                onChatFailure={null}
                                onNewChat={null}
                            />
                        </Flex>
                    </Drawer>
                </ConditionalRender>
                <Flex
                    className={'dashboard' + globalFilterClassName}
                    direction="horizontal"
                    onMouseMove={this.handleMainFlexMouseMove}>
                    <MapTrafficVolumeCacheComponent />
                    <MapPendingEventsComponent />
                    <SvgCommonDefs />
                    <ConditionalRender condition={!AppConstants.hideGlobalMonitoring}>
                        <Flex className={eventTvFilterApplied + '-filter'} direction="vertical" size="12vw">
                            <GlobalMonitoringV2
                                delaySummary={dashboardData?.delaySummary}
                                monitoredEvents={displayedEvents}
                                selectedEvents={this.state.selectedEvents}
                                onSelectedEventsChange={this.handleEventSelectionChange}
                                measures={dashboardData?.monitoredMeasures}
                                tvs={dashboardData?.monitoredTvList}
                                fmps={this.uniqueTvSets((dashboardData as NetworkDashboardData).tvSetMap)}
                                refLocs={this.sortedRefLocs(dashboardData?.monitoredTvList)}
                            />
                        </Flex>
                    </ConditionalRender>
                    <Flex direction="vertical" size="1px" style={{ backgroundColor: 'black' }}>
                        <div
                            style={{
                                position: 'relative',
                                height: '100%',
                                zIndex: 10,
                                display: this.hasFilterEnabled() ? 'block' : 'none',
                            }}>
                            {DashboardUtils.renderFilterComponent(
                                <div
                                    className={
                                        eventTvFilterForward
                                            ? `${eventTvFilterApplied}-filter filter-background ltr`
                                            : `${tvFilterApplied}-filter filter-background rtl`
                                    }
                                />,
                                eventTvFilterVisible,
                                eventTvFilterForward ? 'event-tv-filter' : 'tv-event-filter',
                            )}
                            {DashboardUtils.renderFilterComponent(
                                this.getEventTvFilter(),
                                eventTvFilterVisible,
                                eventTvFilterForward ? 'event-tv-filter-btn' : 'tv-event-filter-btn',
                            )}
                        </div>
                    </Flex>
                    <Flex className={tvFilterApplied + '-filter'}>
                        <NmocNetworkMonitoring
                            currentTime={this.state.currentTime}
                            monitoredPeriod={dashboardData.period}
                            filteredTvIds={this.state.filteredData?.filteredTvs}
                            monitoredTvs={this.getMonitoredTvList(dashboardData)}
                            tvSetMap={(dashboardData as NetworkDashboardData)?.tvSetMap}
                            tvSetProgressMap={(dashboardData as NetworkDashboardData)?.tvSetForecastProgress}
                            tvSetlastUpdate={(dashboardData as NetworkDashboardData)?.tvSetlastUpdate}
                            monitoredMeasures={dashboardData?.monitoredMeasures}
                            monitoredMcdmMeasures={dashboardData?.monitoredMcdmMeasures}
                            coordinationSummaries={dashboardData?.coordinationSummaries}
                            tvsOccupancyPeriods={this.state.selectedFlightsOccupancyPeriods}
                            showMap={this.state.showMap}
                            fullscreenMap={this.state.fullscreenMap}
                            onShowMapChanged={this.handleShowMapChange}
                            onFullscreenMapChanged={this.handleFullscreenMapChange}
                            textFilter={this.props.globalTextFilter}
                            onTextFilterChange={this.handleTextFilterChange}
                            selectedTvs={this.state.selectedTvs}
                            selectedFlights={this.state.selectedFlights}
                            onSelectionChange={this.handleTVSelectionChange}
                            onMapSettingsChanged={this.handleMapSettingChange}
                            mapSettings={this.props.mapSettings}
                            convectionData={selectConvectionData(store.getState())}
                            // convectionAirspaceDef={this.state.convectionAirspaceDef}
                            tvMeteoAlerts={dashboardData?.tvMeteoAlerts}
                            globalFilterEnabled={this.state.globalFilterEnabled}
                            onGlobalFilterEnabledChanged={this.handleGlobalFilterEnabledChange}
                            selectedMeasures={this.state.selectedMeasures}
                            dashboardDataVersion={dashboardData?.version}
                            measureSettings={this.state.measureSettings}
                            displayedMeasures={displayedMeasures}
                            monitoredEvents={MonitoredEventUtils.activeEvents(this.state.monitoredEvents)}
                            hotspots={displayedHotspots}
                            selectedHotspots={this.state.selectedHotspotTvs}
                            regulationOutlook={displayedRegOutlook}
                            selectedRegulationOutlook={this.state.selectedRegOutlooks}
                            onDisplayChanged={this.onDisplayChanged}
                            onFlightLevelFilterChanged={this.onFlightLevelFilterChanged}
                            onMapRefTimePeriodChanged={this.onMapRefTimePeriodChanged}
                        />
                    </Flex>
                    <Flex direction="vertical" size="1px" style={{ backgroundColor: 'black' }}>
                        <div
                            style={{
                                position: 'relative',
                                height: '100%',
                                zIndex: 10,
                                display: this.hasFilterEnabled() ? 'block' : 'none',
                            }}>
                            {/* Filter between TVs and Measures, including hexagonal background */}
                            {DashboardUtils.renderFilterComponent(
                                <div
                                    className={
                                        tvMeasureFilterForward
                                            ? `${tvFilterApplied}-filter filter-background ltr`
                                            : `${measureFilterApplied}-filter filter-background rtl`
                                    }
                                />,
                                tvMeasureFilterVisible,
                                tvMeasureFilterForward ? 'tv-measure-filter' : 'measure-tv-filter',
                            )}
                            {DashboardUtils.renderFilterComponent(
                                this.getTvMeasureFilter(),
                                tvMeasureFilterVisible,
                                tvMeasureFilterForward ? 'tv-measure-filter-btn' : 'measure-tv-filter-btn',
                            )}

                            {/* Clear all filters button */}
                            {DashboardUtils.renderFilterComponent(
                                <ClearButton onClick={() => this.clearFilters()} style={{ width: '4em' }} />,
                                true,
                                'clear-filter',
                            )}

                            {/* Filter between TVs and Flights, including hexagonal background */}
                            {DashboardUtils.renderFilterComponent(
                                <div
                                    className={
                                        tvFlightsFilterForward
                                            ? `${tvFilterApplied}-filter filter-background ltr`
                                            : `${flightFilterApplied}-filter filter-background rtl`
                                    }
                                />,
                                tvFlightFilterVisible,
                                tvFlightsFilterForward ? 'tv-flight-filter' : 'flight-tv-filter',
                            )}
                            {DashboardUtils.renderFilterComponent(
                                this.getTvFlightFilter(),
                                tvFlightFilterVisible,
                                tvFlightsFilterForward ? 'tv-flight-filter-btn' : 'flight-tv-filter-btn',
                            )}
                        </div>
                    </Flex>
                    <Flex direction="vertical" size={AppConstants.dashboardMeasuresFlightsPanelWidth}>
                        <TabManager
                            dashboardData={dashboardData}
                            globalTextFilter={this.props.globalTextFilter}
                            measures={displayedMeasures}
                            currentTime={this.state.currentTime}
                            monitoredEvents={this.state.monitoredEvents}
                            globalFilterEnabled={this.state.globalFilterEnabled}
                            selectedMeasures={this.state.selectedMeasures}
                            measureSettings={this.state.measureSettings}
                            hotspotSettings={this.state.hotspotSettings}
                            selectedHotspotTvs={this.state.selectedHotspotTvs}
                            selectedRegOutlooks={this.state.selectedRegOutlooks}
                            onMeasureSelectionChange={this.handleMeasureSelectionChange}
                            onMeasureSettingsChange={this.handleMeasureSettingsChange}
                            onHotspotSettingsChange={this.handleHotspotSettingsChange}
                            onHotspotSelectionChange={this.handleHotspotSelectionChange}
                            onRegOutlookSelectionChange={this.handleRegOutlookSelectionChange}
                        />
                        <Flex size="1px" style={{ backgroundColor: 'black' }}>
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    zIndex: 10,
                                    display: this.hasFilterEnabled() ? 'block' : 'none',
                                }}>
                                {/* Filter between Measures and Flights, including hexagonal background */}
                                {DashboardUtils.renderFilterComponent(
                                    <div
                                        className={
                                            (measureFlightsFilterForward ? measureFilterApplied : flightFilterApplied) +
                                            '-filter filter-background ttb'
                                        }
                                    />,
                                    measureFlightFilterVisible,
                                    measureFlightsFilterForward ? 'measure-flight-filter' : 'flight-measure-filter',
                                )}
                                {DashboardUtils.renderFilterComponent(
                                    this.getMeasureFlightFilter(),
                                    measureFlightFilterVisible,
                                    measureFlightsFilterForward
                                        ? 'measure-flight-filter-btn'
                                        : 'flight-measure-filter-btn',
                                )}
                            </div>
                        </Flex>
                        <Flex size="50%">
                            <Flex className={flightFilterApplied + '-filter'}>
                                <Flights
                                    flights={this.state.displayedFlights}
                                    showEntryExitColumns={showEntryExitColumns}
                                    selection={this.state.selectedFlights}
                                    onSelectionChange={this.handleFlightSelectionChange}
                                    filterSettings={this.state.flightListFilterSettings}
                                    onFilterSettingsChange={this.handleFlightListFilterSettingsChange}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                    <DashboardModal />
                </Flex>
            </Spin>
        );
    }
}

export default connector(NmocDashboard);
